/**
 * ContactForm — schema-driven marketing-website contact form.
 *
 * The consumer passes a `fields` descriptor that drives rendering:
 *   [
 *     { name: 'name', label: 'Your name', type: 'text', required: true },
 *     { name: 'email', label: 'Email', type: 'email', required: true },
 *     { name: 'phone', label: 'Phone', type: 'phone' },
 *     { name: 'interest', label: 'I want to…', type: 'select',
 *       options: [{value: 'demo', label: 'Schedule a demo'}, …] },
 *     { name: 'message', label: 'Message', type: 'textarea', rows: 5 },
 *   ]
 *
 * A `phone` field renders phone-ui's self-labelling `<PhoneInput>` (PHONE-001): format-as-you-type,
 * a country selector, and E.164 once the number is valid. `'tel'` is not a field kind — the raw
 * HTML tel input is what `web/no-raw-tel-input` forbids. A consumer's validator gates a partial
 * number with `normalizeContactPhone`. The phone kind takes no `helpText` or `placeholder`.
 *
 * The field is phone-ui's LAZY flavor, `PhoneInputLazy` (PERF-FECHUNK-001): PhoneInput carries
 * libphonenumber, and a static import here put that parser into every marketing page's eager
 * vendor chunk, and into every app that imports this package's barrel at all (console reaches it
 * through `CookieConsentProvider`). While it loads, the field keeps its label and its footprint.
 *
 * The form also renders a hidden honeypot field (`website`) and a slot
 * for the consumer's <CaptchaField>. Submission state, errors, and
 * the user-visible outcome message are sourced from the hook returned
 * by `createUseContactForm`.
 *
 * Per CSS-001 + CSS-003: only canonical Track-A utility classes are used.
 * No co-located CSS file ships with this component.
 *
 * @module @puffin-core/marketing-shell/contact/ContactForm
 */

import { useId, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { PhoneInputLazy } from '@puffin-core/phone-ui/lazy';
import { RateLimitNotice } from '@puffin-core/form-security';
import { isRateLimitedOutcome } from './contactOutcomes.js';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

const FIELD_TYPES = Object.freeze(['text', 'email', 'phone', 'url', 'textarea', 'select']);

/**
 * @typedef {Object} ContactFormField
 * @property {string} name
 * @property {string} label
 * @property {'text'|'email'|'phone'|'url'|'textarea'|'select'} type
 * @property {boolean} [required]
 * @property {string} [placeholder]
 * @property {string} [helpText]
 * @property {number} [rows]                  textarea only
 * @property {string} [autoComplete]
 * @property {readonly {value: string, label: string}[]} [options]  select only
 */

/**
 * @param {Object} props
 * @param {Object} props.form                  The hook return value (`useContactForm()`).
 * @param {readonly ContactFormField[]} props.fields
 * @param {string} [props.submitLabel]       Defaults to the `contactForm.submit` catalog word.
 * @param {string} [props.submittingLabel]   Defaults to the `contactForm.submitting` catalog word.
 * @param {React.ReactNode} [props.captchaSlot]   <CaptchaField> mount point.
 * @param {React.ReactNode} [props.successContent] Rendered when isSuccess === true.
 * @param {string} [props.formAriaLabel]     Defaults to the `contactForm.formLabel` catalog word.
 * @param {Function} [props.t] The consumer's translator for the throttle notice (DTOWNRETAIL-SITE-001
 *   U6, I-13), consulted before the `formSecurity` namespace. The hook owns the throttle and hands
 *   its state over as `form.rateLimit`; this form renders the ONE `<RateLimitNotice>` beside the
 *   fields and disables Submit while locked (Q-UMB-21: no throttled form refuses silently). A refusal
 *   is said ONCE: while the throttle is locked the error banner stays off, and when the server refused
 *   the submission as rate-limited the notice carries THAT outcome's sentence (the per-email limiter's
 *   names the address) above its countdown — two `role="alert"` elements for one refusal was measured
 *   on port-web's contact page (DTOWNRETAIL-SITE-001 certification, 2026-10-14).
 */
export default function ContactForm({
  form,
  fields,
  submitLabel,
  submittingLabel,
  captchaSlot,
  successContent,
  formAriaLabel,
  t,
}) {
  // The shell's own words (the honeypot's label, and the button and form name a caller leaves out —
  // never English default parameters); `t` above is the CALLER's translator.
  const shellT = useMarketingShellTranslate();
  const idPrefix = useId();
  const errorBannerRef = useRef(null);

  // Move focus to the error banner whenever the form lands in error state.
  // This is the WCAG-recommended pattern for status messaging on submit.
  const isLocked = Boolean(form.rateLimit && form.rateLimit.isLimited);
  useEffect(() => {
    if (form.isError && errorBannerRef.current) {
      errorBannerRef.current.focus();
    }
  }, [form.isError]);

  function handleSubmit(event) {
    event.preventDefault();
    form.submit();
  }

  if (form.isSuccess && successContent) {
    return <div className="contact-form-success-slot">{successContent}</div>;
  }

  return (
    <form
      className="contact-form"
      noValidate
      aria-label={formAriaLabel ?? shellT('contactForm.formLabel')}
      onSubmit={handleSubmit}
    >
      {form.isError && form.message && !isLocked ? (
        <div
          ref={errorBannerRef}
          role="alert"
          aria-live="assertive"
          tabIndex={-1}
          className="contact-form-error-banner"
        >
          {form.message}
        </div>
      ) : null}

      {form.rateLimit ? (
        <RateLimitNotice
          rateLimit={form.rateLimit}
          message={isRateLimitedOutcome(form.outcome) ? form.message : undefined}
          t={t}
        />
      ) : null}

      {fields.map((field) => (
        <ContactFormField key={field.name} field={field} form={form} idPrefix={idPrefix} />
      ))}

      {/* Honeypot — visually hidden via .sr-only utility but reachable
          by bots that crawl the form. A non-empty value short-circuits
          the submit handler with the `honeypot` outcome. */}
      <div className="sr-only" aria-hidden="true">
        <label htmlFor={`${idPrefix}-website`}>
          {shellT('contactForm.honeypot')}
          <input
            id={`${idPrefix}-website`}
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.fields.website ?? ''}
            onChange={(e) => form.setField('website', e.target.value)}
          />
        </label>
      </div>

      {captchaSlot ? <div className="contact-form-captcha-slot">{captchaSlot}</div> : null}

      <button
        type="submit"
        className="btn btn-marketing-primary btn-marketing-lg"
        disabled={form.isSubmitting || Boolean(form.rateLimit && form.rateLimit.isLimited)}
      >
        {form.isSubmitting
          ? (submittingLabel ?? shellT('contactForm.submitting'))
          : (submitLabel ?? shellT('contactForm.submit'))}
      </button>
    </form>
  );
}

/**
 * ContactFormField — one labelled control (input | textarea | select) plus its
 * optional required-marker, help text, and field-level error message. Extracted
 * to module scope so <ContactForm> renders a flat list of fields and never nests
 * the per-option map inside the per-field map.
 *
 * Owns the id/aria wiring for its own field: `aria-describedby` points at the
 * help text and/or the error, and `aria-invalid` is set only when invalid.
 */
function ContactFormField({ field, form, idPrefix }) {
  if (field.type === 'phone') {
    return (
      <div className="contact-form-field">
        <PhoneInputLazy
          name={field.name}
          label={field.label}
          value={form.fields[field.name] ?? ''}
          onChange={(value) => form.setField(field.name, value || '')}
          error={form.errors[field.name]}
          required={field.required}
          autoComplete={field.autoComplete}
        />
      </div>
    );
  }
  const fieldId = `${idPrefix}-${field.name}`;
  const errorId = `${fieldId}-error`;
  const error = form.errors[field.name];
  const isInvalid = Boolean(error);
  const helpId = field.helpText ? `${fieldId}-help` : null;
  const ariaDescribedBy =
    [helpId, isInvalid ? errorId : null].filter(Boolean).join(' ') || undefined;
  const commonProps = {
    id: fieldId,
    name: field.name,
    required: field.required,
    value: form.fields[field.name] ?? '',
    onChange: (e) => form.setField(field.name, e.target.value),
    'aria-invalid': isInvalid || undefined,
    'aria-describedby': ariaDescribedBy,
    autoComplete: field.autoComplete,
  };

  const requiredMarker = field.required ? (
    <span aria-hidden="true" className="contact-form-required-marker">
      *
    </span>
  ) : null;

  const control =
    field.type === 'textarea' ? (
      <textarea
        {...commonProps}
        rows={field.rows || 5}
        placeholder={field.placeholder}
        className="form-textarea"
      />
    ) : field.type === 'select' ? (
      <select {...commonProps} className="form-select">
        {field.placeholder ? (
          <option value="" disabled>
            {field.placeholder}
          </option>
        ) : null}
        {(field.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    ) : (
      <input
        {...commonProps}
        type={field.type}
        placeholder={field.placeholder}
        className="form-input"
      />
    );

  const helpText = field.helpText ? (
    <p id={helpId} className="form-help-text">
      {field.helpText}
    </p>
  ) : null;

  const fieldError = isInvalid ? (
    <p id={errorId} className="form-field-error" role="alert">
      {error}
    </p>
  ) : null;

  return (
    <div className="contact-form-field">
      <label htmlFor={fieldId} className="form-label">
        {field.label}
        {requiredMarker}
      </label>
      {control}
      {helpText}
      {fieldError}
    </div>
  );
}

ContactFormField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(FIELD_TYPES).isRequired,
    required: PropTypes.bool,
    placeholder: PropTypes.string,
    helpText: PropTypes.string,
    autoComplete: PropTypes.string,
    rows: PropTypes.number,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  form: PropTypes.shape({
    fields: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setField: PropTypes.func.isRequired,
  }).isRequired,
  idPrefix: PropTypes.string.isRequired,
};

ContactForm.propTypes = {
  form: PropTypes.shape({
    fields: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setField: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isSuccess: PropTypes.bool.isRequired,
    isError: PropTypes.bool.isRequired,
    message: PropTypes.string,
    // The throttle's state (`useRateLimit()`), passed whole to `<RateLimitNotice>`.
    rateLimit: PropTypes.shape({
      isLimited: PropTypes.bool.isRequired,
      attemptCount: PropTypes.number.isRequired,
      remainingAttempts: PropTypes.number.isRequired,
      timeUntilReset: PropTypes.number.isRequired,
    }),
  }).isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(FIELD_TYPES).isRequired,
      required: PropTypes.bool,
      placeholder: PropTypes.string,
      helpText: PropTypes.string,
      rows: PropTypes.number,
      autoComplete: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
        }),
      ),
    }),
  ).isRequired,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
  captchaSlot: PropTypes.node,
  successContent: PropTypes.node,
  formAriaLabel: PropTypes.string,
  t: PropTypes.func,
};

ContactForm.FIELD_TYPES = FIELD_TYPES;
